import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { disconnectConnector, fetchBootstrap } from "./api";
import { openConnectorOAuthTab } from "./oauthConnect";
import { useSessionStore } from "./session";
import { GA4PropertyPickerModal } from "./GA4PropertyPickerModal";
import { MetaAccountPickerModal } from "./MetaAccountPickerModal";
import { providerLogoFor } from "./SourceLogos";
import type { SourceState } from "./types";

/**
 * Private-beta gate embedded in the TN admin iframe instead of the dashboard.
 * Stores can install the app (the callback exchanges the OAuth code and the
 * backend persists the encrypted token, lifecycle webhooks register), but the
 * merchant sees this screen instead of the unfinished product surface.
 *
 * Activated by build-time env var `VITE_PREVIEW_MODE=true` (off by default).
 * Sits inside the post-Nexo / post-SessionGate tree, so the app JWT exists and
 * the gate can drive the authenticated connector flow: while we finish the
 * product, the merchant can connect Google Analytics 4 and Meta Ads so we have
 * real tokens to validate the agents internally.
 *
 * The connect flow opens the provider consent screen in a NEW TAB
 * (`openConnectorOAuthTab`) because Google/Meta refuse to render inside the
 * admin iframe. The OAuth return lands in that separate tab; when the merchant
 * switches back here we refetch and prompt the ad-account / property picker
 * (which runs in THIS authenticated surface — the new standalone tab has no
 * session of its own).
 */

interface ConnectCardSpec {
  kind: string;
  name: string;
  blurb: string;
}

const CONNECT_CARDS: ConnectCardSpec[] = [
  {
    kind: "google_analytics_4",
    name: "Google Analytics 4",
    blurb: "Tráfico, adquisición y conversiones de tu sitio.",
  },
  {
    kind: "meta_ads",
    name: "Meta Ads",
    blurb: "Campañas y resultados de Facebook e Instagram.",
  },
];

export default function PreviewModeGate() {
  const queryClient = useQueryClient();
  const accessToken = useSessionStore((s) => s.accessToken);

  const { data } = useQuery({
    queryKey: ["bootstrap"],
    queryFn: () => fetchBootstrap(accessToken as string),
    enabled: Boolean(accessToken),
  });

  const sourcesByKind = useMemo(() => {
    const map: Record<string, SourceState> = {};
    for (const source of data?.sources ?? []) map[source.kind] = source;
    return map;
  }, [data]);

  const [connectingKind, setConnectingKind] = useState<string | null>(null);
  const [connectError, setConnectError] = useState<string | null>(null);
  const [ga4PickerOpen, setGa4PickerOpen] = useState(false);
  const [metaPickerOpen, setMetaPickerOpen] = useState(false);
  // The connector whose consent tab we just opened. We use it to (a) refetch
  // on return and (b) auto-open the matching picker once the credential lands.
  const pendingKindRef = useRef<string | null>(null);

  const refreshSources = useCallback(() => {
    void queryClient.invalidateQueries({ queryKey: ["bootstrap"] });
  }, [queryClient]);

  const openPickerFor = useCallback((kind: string) => {
    if (kind === "google_analytics_4") setGa4PickerOpen(true);
    else if (kind === "meta_ads") setMetaPickerOpen(true);
  }, []);

  async function handleConnect(kind: string) {
    setConnectError(null);
    setConnectingKind(kind);
    pendingKindRef.current = kind;
    try {
      await openConnectorOAuthTab(kind);
    } catch {
      setConnectError(
        "No pudimos abrir la conexión. Revisá que no se haya bloqueado la ventana y probá de nuevo.",
      );
      pendingKindRef.current = null;
    } finally {
      setConnectingKind(null);
    }
  }

  // When the merchant returns to the admin tab after the consent flow, refetch
  // so the card reflects the freshly-persisted credential.
  useEffect(() => {
    function onReturn() {
      if (pendingKindRef.current && document.visibilityState === "visible") {
        refreshSources();
      }
    }
    window.addEventListener("focus", onReturn);
    document.addEventListener("visibilitychange", onReturn);
    return () => {
      window.removeEventListener("focus", onReturn);
      document.removeEventListener("visibilitychange", onReturn);
    };
  }, [refreshSources]);

  // Once the pending connector reports it needs a selection, open its picker
  // automatically (one shot — cleared by nulling the ref).
  useEffect(() => {
    const kind = pendingKindRef.current;
    if (!kind) return;
    if (sourcesByKind[kind]?.needs_selection) {
      openPickerFor(kind);
      pendingKindRef.current = null;
    }
  }, [sourcesByKind, openPickerFor]);

  return (
    <main style={STYLES.main}>
      <section style={STYLES.card}>
        <span style={STYLES.badge}>agentes nube</span>
        <h1 style={STYLES.title}>Estamos construyendo algo increíble</h1>
        <p style={STYLES.body}>
          Gracias por sumarte. Usamos la información que compartís para darle
          forma al producto y construir algo a la medida de tu tienda.
        </p>
        <p style={STYLES.body}>Te avisamos apenas tu equipo esté listo.</p>

        <div style={STYLES.connectPanel}>
          <p style={STYLES.connectKicker}>Mientras tanto</p>
          <h2 style={STYLES.connectTitle}>Conectá tus fuentes</h2>
          <p style={STYLES.connectSubtitle}>
            Sumá estas fuentes para que tus agentes lleguen con más contexto
            desde el primer día.
          </p>

          <div style={STYLES.cardGrid}>
            {CONNECT_CARDS.map((spec) => (
              <ConnectCard
                key={spec.kind}
                spec={spec}
                source={sourcesByKind[spec.kind]}
                connecting={connectingKind === spec.kind}
                disabled={connectingKind !== null && connectingKind !== spec.kind}
                onConnect={() => handleConnect(spec.kind)}
                onPick={() => openPickerFor(spec.kind)}
                onDisconnected={refreshSources}
              />
            ))}
          </div>

          {connectError ? (
            <p style={STYLES.error} role="alert">
              {connectError}
            </p>
          ) : null}
        </div>

        <p style={STYLES.footer}>
          ¿Consultas? Escribinos a{" "}
          <a href="mailto:hola@appsnube.com" style={STYLES.link}>
            hola@appsnube.com
          </a>
        </p>
      </section>

      <GA4PropertyPickerModal
        open={ga4PickerOpen}
        onClose={() => setGa4PickerOpen(false)}
        onSelected={refreshSources}
      />
      <MetaAccountPickerModal
        open={metaPickerOpen}
        onClose={() => setMetaPickerOpen(false)}
        onSelected={refreshSources}
      />
    </main>
  );
}

function ConnectCard({
  spec,
  source,
  connecting,
  disabled,
  onConnect,
  onPick,
  onDisconnected,
}: {
  spec: ConnectCardSpec;
  source: SourceState | undefined;
  connecting: boolean;
  disabled: boolean;
  onConnect: () => void;
  onPick: () => void;
  onDisconnected: () => void;
}) {
  const logo = providerLogoFor(spec.kind, 26);
  const state = source?.state ?? "disconnected";
  const needsSelection = Boolean(source?.needs_selection);
  const isConnected =
    !needsSelection && (state === "healthy" || state === "connected");
  const changeLabel =
    spec.kind === "google_analytics_4" ? "Cambiar propiedad" : "Cambiar cuenta";

  const [confirmingDisconnect, setConfirmingDisconnect] = useState(false);
  const disconnect = useMutation({
    mutationFn: () => disconnectConnector(spec.kind),
    onSuccess: () => {
      setConfirmingDisconnect(false);
      onDisconnected();
    },
  });

  let statusLabel = "Sin conectar";
  let statusTone: keyof typeof STATUS_DOT = "idle";
  if (needsSelection) {
    statusLabel = "Falta elegir cuenta";
    statusTone = "warn";
  } else if (isConnected) {
    statusLabel = "Conectado";
    statusTone = "ok";
  } else if (state === "expired") {
    statusLabel = "Acceso vencido";
    statusTone = "warn";
  } else if (state === "error" || state === "degraded" || state === "limited") {
    statusLabel = "Con problemas";
    statusTone = "warn";
  }

  return (
    <article style={STYLES.connectCard}>
      <div style={STYLES.cardHeader}>
        <span style={STYLES.logoWrap} aria-hidden="true">
          {logo ?? spec.name.slice(0, 2).toUpperCase()}
        </span>
        <div style={STYLES.cardHeadText}>
          <p style={STYLES.cardName}>{spec.name}</p>
          <span style={STYLES.statusRow}>
            <span
              style={{ ...STYLES.statusDot, background: STATUS_DOT[statusTone] }}
            />
            <span style={STYLES.statusLabel}>{statusLabel}</span>
          </span>
        </div>
      </div>
      <p style={STYLES.cardBlurb}>{spec.blurb}</p>

      {isConnected ? (
        confirmingDisconnect ? (
          <div style={STYLES.confirmCol}>
            <span style={STYLES.confirmText}>
              ¿Desconectar {spec.name}? Tu equipo deja de leer esta fuente.
            </span>
            <div style={STYLES.btnRow}>
              <button
                type="button"
                style={STYLES.ctaGhost}
                onClick={() => setConfirmingDisconnect(false)}
                disabled={disconnect.isPending}
              >
                Cancelar
              </button>
              <button
                type="button"
                style={STYLES.ctaDanger}
                onClick={() => disconnect.mutate()}
                disabled={disconnect.isPending}
              >
                {disconnect.isPending ? "Desconectando…" : "Sí, desconectar"}
              </button>
            </div>
          </div>
        ) : (
          <div style={STYLES.btnRow}>
            <button type="button" style={STYLES.ctaGhost} onClick={onPick}>
              {changeLabel}
            </button>
            <button
              type="button"
              style={STYLES.ctaGhostDanger}
              onClick={() => setConfirmingDisconnect(true)}
            >
              Desconectar
            </button>
          </div>
        )
      ) : needsSelection ? (
        <button type="button" style={STYLES.ctaPrimary} onClick={onPick}>
          {spec.kind === "google_analytics_4"
            ? "Elegí tu propiedad"
            : "Elegí tu cuenta"}
        </button>
      ) : (
        <button
          type="button"
          style={disabled ? STYLES.ctaDisabled : STYLES.ctaPrimary}
          onClick={onConnect}
          disabled={connecting || disabled}
        >
          {connecting
            ? "Abriendo…"
            : state === "disconnected"
              ? "Conectar"
              : "Reconectar"}
        </button>
      )}
    </article>
  );
}

const STATUS_DOT = {
  idle: "#c2c9d6",
  ok: "#1e9e6a",
  warn: "#d98a00",
} as const;

const STYLES: Record<string, React.CSSProperties> = {
  main: {
    minHeight: "100vh",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: 24,
    background:
      "radial-gradient(1200px 600px at 50% -10%, #eef2ff 0%, #f7f8fb 45%, #ffffff 100%)",
    color: "#0f1729",
    fontFamily:
      '"Geist Sans", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
    WebkitFontSmoothing: "antialiased",
  },
  card: {
    width: "100%",
    maxWidth: 560,
    textAlign: "center",
    lineHeight: 1.55,
  },
  badge: {
    display: "inline-block",
    fontSize: 12,
    fontWeight: 600,
    letterSpacing: "0.16em",
    textTransform: "uppercase",
    color: "#6366f1",
    marginBottom: 20,
  },
  title: {
    fontSize: 32,
    fontWeight: 650,
    margin: "0 0 16px",
    letterSpacing: "-0.02em",
    lineHeight: 1.15,
  },
  body: {
    fontSize: 16,
    color: "#475067",
    margin: "0 auto 12px",
    maxWidth: 460,
  },
  connectPanel: {
    marginTop: 36,
    padding: "28px 24px",
    background: "#ffffff",
    border: "1px solid #e7eaf1",
    borderRadius: 18,
    boxShadow: "0 12px 40px -24px rgba(15,23,41,0.35)",
    textAlign: "left",
  },
  connectKicker: {
    fontSize: 12,
    fontWeight: 600,
    letterSpacing: "0.08em",
    textTransform: "uppercase",
    color: "#9aa2b4",
    margin: "0 0 4px",
  },
  connectTitle: {
    fontSize: 19,
    fontWeight: 620,
    margin: "0 0 6px",
    letterSpacing: "-0.01em",
  },
  connectSubtitle: {
    fontSize: 14,
    color: "#5b6479",
    margin: "0 0 20px",
  },
  cardGrid: {
    display: "grid",
    gridTemplateColumns: "1fr 1fr",
    gap: 14,
  },
  connectCard: {
    display: "flex",
    flexDirection: "column",
    gap: 10,
    padding: 16,
    border: "1px solid #e7eaf1",
    borderRadius: 14,
    background: "#fbfcfe",
  },
  cardHeader: {
    display: "flex",
    alignItems: "center",
    gap: 10,
  },
  logoWrap: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    width: 38,
    height: 38,
    borderRadius: 10,
    background: "#ffffff",
    border: "1px solid #eceef4",
    fontSize: 12,
    fontWeight: 700,
    color: "#475067",
    flexShrink: 0,
  },
  cardHeadText: {
    display: "flex",
    flexDirection: "column",
    gap: 2,
    minWidth: 0,
  },
  cardName: {
    fontSize: 14,
    fontWeight: 600,
    margin: 0,
    color: "#0f1729",
  },
  statusRow: { display: "inline-flex", alignItems: "center", gap: 6 },
  statusDot: {
    width: 7,
    height: 7,
    borderRadius: "50%",
    display: "inline-block",
  },
  statusLabel: { fontSize: 12, color: "#6b7488" },
  cardBlurb: {
    fontSize: 13,
    color: "#5b6479",
    margin: 0,
    flex: 1,
  },
  ctaPrimary: {
    appearance: "none",
    border: "none",
    borderRadius: 10,
    padding: "9px 14px",
    fontSize: 14,
    fontWeight: 600,
    color: "#ffffff",
    background: "#4f46e5",
    cursor: "pointer",
  },
  ctaDisabled: {
    appearance: "none",
    border: "none",
    borderRadius: 10,
    padding: "9px 14px",
    fontSize: 14,
    fontWeight: 600,
    color: "#ffffff",
    background: "#b9bdd0",
    cursor: "not-allowed",
  },
  ctaDone: {
    appearance: "none",
    border: "1px solid #c8ead9",
    borderRadius: 10,
    padding: "9px 14px",
    fontSize: 14,
    fontWeight: 600,
    color: "#1e9e6a",
    background: "#eefaf3",
    cursor: "default",
  },
  btnRow: {
    display: "flex",
    flexWrap: "wrap",
    alignItems: "center",
    gap: 8,
    width: "100%",
  },
  ctaGhost: {
    appearance: "none",
    border: "1px solid #e7eaf1",
    borderRadius: 10,
    padding: "8px 12px",
    fontSize: 13.5,
    fontWeight: 600,
    color: "#475067",
    background: "#ffffff",
    cursor: "pointer",
  },
  ctaGhostDanger: {
    appearance: "none",
    border: "1px solid #f1d9d6",
    borderRadius: 10,
    padding: "8px 12px",
    fontSize: 13.5,
    fontWeight: 600,
    color: "#b42318",
    background: "#ffffff",
    cursor: "pointer",
    marginLeft: "auto",
  },
  ctaDanger: {
    appearance: "none",
    border: "none",
    borderRadius: 10,
    padding: "9px 14px",
    fontSize: 14,
    fontWeight: 600,
    color: "#ffffff",
    background: "#d92d20",
    cursor: "pointer",
  },
  confirmCol: {
    display: "flex",
    flexDirection: "column",
    gap: 8,
    width: "100%",
  },
  confirmText: {
    margin: 0,
    fontSize: 13,
    lineHeight: 1.45,
    color: "#5b6479",
  },
  error: {
    marginTop: 16,
    marginBottom: 0,
    fontSize: 13,
    color: "#b42318",
  },
  footer: {
    marginTop: 32,
    fontSize: 14,
    color: "#9aa2b4",
  },
  link: {
    color: "#6b7488",
    textDecoration: "none",
    borderBottom: "1px solid #e5e7eb",
  },
};
